import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, resolveImg, inr, formatApiError } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useCustomerAuth } from "../context/CustomerAuthContext";

const ADVANCE = 200;

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const Field = ({ label, name, value, onChange, required, type = "text", testid, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="block text-sm font-medium text-[#1C2B20] mb-1.5">
      {label} {required && <span className="text-[#C86D51]">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testid}
      className="w-full rounded-xl border border-[#E2DAD0] bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F4D2E]"
    />
  </div>
);

const Checkout = () => {
  const { items, subtotal, clearCart } = useCart();
  const { customer } = useCustomerAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState({ razorpay_enabled: false, advance_amount: ADVANCE });
  const [paymentMethod, setPaymentMethod] = useState("cod_advance");
  const [processing, setProcessing] = useState(false);
  const placedRef = React.useRef(false);
  const [form, setForm] = useState({
    customer_name: "", phone: "", email: "", address: "", city: "", pincode: "", state: "",
  });

  useEffect(() => {
    if (items.length === 0 && !placedRef.current) navigate("/cart");
  }, [items.length, navigate]);

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (customer) {
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || customer.name || "",
        email: f.email || customer.email || "",
      }));
    }
  }, [customer]);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const payNow = paymentMethod === "online_full" ? subtotal : config.advance_amount || ADVANCE;
  const balance = paymentMethod === "online_full" ? 0 : Math.max(subtotal - (config.advance_amount || ADVANCE), 0);

  const finishSuccess = (orderId) => {
    placedRef.current = true;
    navigate(`/order-success/${orderId}`);
    clearCart();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.phone || !form.address) {
      toast.error("Please fill in name, phone and address.");
      return;
    }
    setProcessing(true);
    try {
      // 1. create order
      const orderRes = await api.post("/orders", {
        ...form,
        payment_method: paymentMethod,
        items: items.map((i) => ({
          product_id: i.product_id, name: i.name, price: i.price,
          quantity: i.quantity, image_url: i.image_url,
        })),
      });
      const order = orderRes.data;

      // 2. create payment
      const payRes = await api.post(`/orders/${order.id}/create-payment`);
      const pay = payRes.data;

      if (pay.demo) {
        // Demo mode - simulate advance payment success
        await api.post("/orders/verify-payment", { order_id: order.id });
        toast.success("Advance payment confirmed (demo mode)");
        finishSuccess(order.id);
        return;
      }

      // 3. real razorpay checkout
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        toast.error("Failed to load Razorpay. Please try again.");
        setProcessing(false);
        return;
      }
      const rzp = new window.Razorpay({
        key: pay.key_id,
        amount: pay.amount,
        currency: pay.currency,
        order_id: pay.razorpay_order_id,
        name: "Aarmira SuperNaturals",
        description: paymentMethod === "online_full" ? "Full Payment" : "₹200 Advance Payment",
        prefill: { name: form.customer_name, contact: form.phone },
        theme: { color: "#1F4D2E" },
        handler: async (resp) => {
          try {
            await api.post("/orders/verify-payment", {
              order_id: order.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment successful! Order confirmed.");
            finishSuccess(order.id);
          } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
            setProcessing(false);
          }
        },
        modal: { ondismiss: () => setProcessing(false) },
      });
      rzp.open();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setProcessing(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="font-serif font-bold text-3xl sm:text-4xl text-[#1C2B20] mb-8">Checkout</h1>
      <form onSubmit={handleSubmit} className="grid lg:grid-cols-[1fr_380px] gap-8">
        {/* Left - details */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-[#E2DAD0] p-6">
            <h2 className="font-serif text-xl font-bold text-[#1C2B20] mb-5">Delivery Details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full Name" name="customer_name" value={form.customer_name} onChange={handleChange} required testid="checkout-name" full />
              <Field label="Phone Number" name="phone" value={form.phone} onChange={handleChange} required type="tel" testid="checkout-phone" />
              <Field label="Email (for order updates)" name="email" value={form.email} onChange={handleChange} type="email" testid="checkout-email" />
              <Field label="Delivery Address" name="address" value={form.address} onChange={handleChange} required testid="checkout-address" full />
              <Field label="City" name="city" value={form.city} onChange={handleChange} testid="checkout-city" />
              <Field label="State" name="state" value={form.state} onChange={handleChange} testid="checkout-state" />
              <Field label="Pincode" name="pincode" value={form.pincode} onChange={handleChange} testid="checkout-pincode" />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[#E2DAD0] p-6">
            <h2 className="font-serif text-xl font-bold text-[#1C2B20] mb-5">Payment Method</h2>
            <div className="space-y-3">
              <label
                className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-colors ${
                  paymentMethod === "cod_advance" ? "border-[#1F4D2E] bg-[#F5F0E6]" : "border-[#E2DAD0]"
                }`}
                data-testid="checkout-payment-cod-advance"
              >
                <input
                  type="radio"
                  name="payment"
                  checked={paymentMethod === "cod_advance"}
                  onChange={() => setPaymentMethod("cod_advance")}
                  className="mt-1 accent-[#1F4D2E]"
                />
                <div>
                  <div className="font-semibold text-[#1C2B20] flex items-center gap-2">
                    Cash on Delivery + ₹200 Advance
                    <span className="bg-[#C5A059] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">RECOMMENDED</span>
                  </div>
                  <p className="text-sm text-[#5A6B5D] mt-1">
                    Pay a non-refundable ₹200 advance now to confirm your order. Remaining {inr(balance)} collected on delivery.
                  </p>
                </div>
              </label>

              <label
                className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-colors ${
                  paymentMethod === "online_full" ? "border-[#1F4D2E] bg-[#F5F0E6]" : "border-[#E2DAD0]"
                }`}
                data-testid="checkout-payment-razorpay"
              >
                <input
                  type="radio"
                  name="payment"
                  checked={paymentMethod === "online_full"}
                  onChange={() => setPaymentMethod("online_full")}
                  className="mt-1 accent-[#1F4D2E]"
                />
                <div>
                  <div className="font-semibold text-[#1C2B20]">Pay Full Amount Online</div>
                  <p className="text-sm text-[#5A6B5D] mt-1">Securely pay the full {inr(subtotal)} now via Razorpay (UPI / Cards / Netbanking).</p>
                </div>
              </label>
            </div>
            {!config.razorpay_enabled && (
              <p className="mt-4 text-xs text-[#C86D51] bg-[#C86D51]/10 rounded-lg p-3" data-testid="razorpay-demo-notice">
                Razorpay is in demo mode (no live keys yet). Payments will be simulated for now. Add your Razorpay keys to enable real payments.
              </p>
            )}
          </div>
        </div>

        {/* Right - summary */}
        <div className="lg:sticky lg:top-24 h-fit bg-white rounded-2xl border border-[#E2DAD0] p-6">
          <h2 className="font-serif text-xl font-bold text-[#1C2B20] mb-5">Your Order</h2>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {items.map((i) => (
              <div key={i.product_id} className="flex gap-3 items-center">
                <img src={resolveImg(i.image_url)} alt={i.name} className="h-12 w-12 rounded-lg object-cover bg-[#FAF7F2]" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#1C2B20] truncate">{i.name}</p>
                  <p className="text-xs text-[#8A9A8D]">Qty {i.quantity}</p>
                </div>
                <span className="text-sm font-semibold text-[#1C2B20]">{inr(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-[#E2DAD0] mt-4 pt-4 space-y-2 text-sm">
            <div className="flex justify-between text-[#5A6B5D]">
              <span>Order Total</span>
              <span className="font-semibold text-[#1C2B20]">{inr(subtotal)}</span>
            </div>
            <div className="flex justify-between text-[#5A6B5D]">
              <span>{paymentMethod === "online_full" ? "Paying now" : "Advance (pay now)"}</span>
              <span className="font-semibold text-[#1F4D2E]" data-testid="checkout-pay-now">{inr(payNow)}</span>
            </div>
            {paymentMethod === "cod_advance" && (
              <div className="flex justify-between text-[#5A6B5D]">
                <span>Balance on delivery</span>
                <span className="font-semibold text-[#1C2B20]">{inr(balance)}</span>
              </div>
            )}
          </div>
          <button
            type="submit"
            disabled={processing}
            data-testid="checkout-place-order-btn"
            className="mt-6 w-full flex items-center justify-center gap-2 bg-[#1F4D2E] hover:bg-[#163A22] disabled:opacity-60 text-white font-medium rounded-full py-3.5 transition-colors"
          >
            {processing ? <Loader2 size={18} className="animate-spin" /> : <Lock size={16} />}
            {processing ? "Processing..." : `Pay ${inr(payNow)} & Place Order`}
          </button>
          <p className="mt-3 text-xs text-[#8A9A8D] flex items-center justify-center gap-1.5">
            <CheckCircle2 size={14} className="text-[#1F4D2E]" /> Secure checkout
          </p>
        </div>
      </form>
    </div>
  );
};

export default Checkout;
